// src/components/GiftPickerModal.js
import React, { useMemo, useState, useContext, useEffect } from "react";
import { AuthContext } from "../App";
import apiClient from "../api/client";
import EliteBadge from "./elite/EliteBadge";
import { toast } from "sonner";
import { X } from "lucide-react";
import { normalizeList } from "../utils/normalize";


export default function GiftPickerModal({
  open,  // ← Changed from isOpen to match prop from Chat.js
  onClose,
  targetUserId,
  matchId,
  recipientName = "them",
  
  // NEW: when true, send via /api/lounge/gifts/send
  isLoungeGift = false,
  // NEW: needed for lounge gifts
  loungeRoomKey,
}) {
  const { token, user } = useContext(AuthContext);
  const [gifts, setGifts] = useState([]);
  const [balance, setBalance] = useState(0);
  const [sendingKey, setSendingKey] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [showStore, setShowStore] = useState(false);
  const [packs, setPacks] = useState([]);
  const [loadingPacks, setLoadingPacks] = useState(false);

  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const catalogRes = await apiClient.get(`/gifts/catalog`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGifts(normalizeList(catalogRes.data, ['gifts', 'catalog']));

        const balanceRes = await apiClient.get(`/wallet/lovecoins`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setBalance(balanceRes.data?.balance || 0);
      } catch (err) {
        console.error("Failed to load gifts:", err);
        setError("Failed to load gifts. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, token]);

  const { standardGifts, eliteGifts } = useMemo(() => {
    const standard = [];
    const elite = [];
    (gifts || []).forEach((g) => {
      if (g.elite_only) elite.push(g);
      else standard.push(g);
    });
    return { standardGifts: standard, eliteGifts: elite };
  }, [gifts]);

  if (!open) return null;

  // Check Elite status more comprehensively
  const isElite = user?.is_elite || 
                  user?.elite || 
                  user?.subscription_tier === 'elite' ||
                  user?.subscription_tier === 'elite_plus' ||
                  user?.entitlements?.elite ||
                  user?.entitlements?.elite_plus ||
                  false;

  const handleGiftClick = async (gift) => {
    setError("");
    
    // Extra safety check
    if (!targetUserId) {
      console.warn("[Gift] Missing targetUserId, aborting send");
      toast.error("No recipient selected for this gift.");
      return;
    }
    
    if (!isElite && gift.elite_only) {
      setError("Upgrade to Elite to send this exclusive gift.");
      return;
    }
    if (gift.cost > balance) {
      setError("You do not have enough LoveCoins to send this gift.");
      return;
    }

    try {
      setSendingKey(gift.key);
      
      let response;
      
      if (isLoungeGift) {
        // 🔥 LOUNGE GIFTS → /api/lounge/gifts/send
        console.log("[Gift] Sending lounge gift", {
          room_key: loungeRoomKey,
          to_user_id: targetUserId,
          gift_key: gift.key,
        });
        
        response = await apiClient.post(
          `/lounge/gifts/send`,
          {
            room_key: loungeRoomKey,
            to_user_id: targetUserId,
            gift_key: gift.key,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else {
        // 💌 1-to-1 CHAT GIFTS → /api/gifts/send (existing behavior)
        console.log("[Gift] Sending 1-to-1 gift", {
          match_id: matchId,
          to_user_id: targetUserId,
          gift_key: gift.key,
        });
        
        response = await apiClient.post(
          `/gifts/send`,
          {
            match_id: matchId,
            to_user_id: targetUserId,
            gift_key: gift.key,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
      
      // ✅ Update balance from response
      const newBalance = response?.data?.new_balance ?? response?.data?.balance ?? response?.data?.newBalance;
      
      if (newBalance !== null && newBalance !== undefined) {
        console.log("[Gift] ✅ New balance from server:", newBalance);
        setBalance(newBalance);
      } else {
        setBalance((prev) => prev - gift.cost);
      }
      
      // ✅ Immediate visual feedback
      toast.success(
        `You sent ${gift.emoji} ${gift.name} to ${recipientName}!`,
        {
          duration: 3000,
          position: 'top-center',
        }
      );
      
      console.log(`[Gift] ✅ Sent ${gift.name} to ${recipientName}. New balance: ${newBalance}`);
      
      // Close modal after brief delay so user sees the feedback
      setTimeout(() => {
        if (onClose) onClose();
      }, 400);
      
    } catch (err) {
      console.error('[Gift] ❌ Failed to send:', err);
      const errorMsg = err?.response?.data?.detail || err?.response?.data?.message || "Unable to send gift. Please try again.";
      setError(errorMsg);
      toast.error(errorMsg, {
        duration: 4000,
        position: 'top-center',
      });
    } finally {
      setSendingKey(null);
    }
  };

  const loadPacks = async () => {
    try {
      setLoadingPacks(true);
      const res = await apiClient.get(`/lovecoins/packs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPacks(res.data?.packs || []);
    } catch (err) {
      console.error("[LoveCoins] failed to load packs", err);
      toast.error("Unable to load LoveCoin packs right now.");
    } finally {
      setLoadingPacks(false);
    }
  };

  const handleOpenStore = () => {
    setShowStore(true);
    if (!packs.length) {
      loadPacks();
    }
  };

  const handleDevPurchase = async (packKey) => {
    try {
      const res = await apiClient.post(
        `/lovecoins/dev-purchase`,
        { pack_key: packKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newBalance = res.data?.newBalance;
      if (typeof newBalance === "number") {
        setBalance(newBalance);
      }
      toast.success("LoveCoins added to your balance!");
    } catch (err) {
      console.error("[LoveCoins] dev purchase failed", err);
      toast.error("Could not complete LoveCoin purchase.");
    }
  };

  const handleBuyCoins = () => {
    handleOpenStore();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="w-[94%] max-w-md rounded-3xl bg-gradient-to-b from-slate-950 via-slate-900 to-black border border-white/10 shadow-2xl p-4 sm:p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center gap-2">
              Send a Gift to {recipientName} 🎁
              {isElite && <EliteBadge size="sm" />}
            </h2>
            <p className="text-xs sm:text-sm text-white/70 mt-1">
              Balance:{" "}
              <span className="font-semibold text-pink-300">
                {balance.toLocaleString()} LoveCoins
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/60 hover:text-white text-xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Loading */}
        {loading && <div className="mt-6 text-center text-white/60">Loading gifts...</div>}

        {/* Error */}
        {error && (
          <div className="mt-3 rounded-2xl bg-red-500/10 border border-red-500/50 px-3 py-2 text-xs sm:text-sm text-red-100">
            {error}
          </div>
        )}

        {/* Standard Gifts */}
        {!loading && standardGifts.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xs sm:text-sm text-white/80 font-semibold">Gifts</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
              {standardGifts.map((gift) => {
                const disabled =
                  sendingKey === gift.key || (gift.cost || 0) > (balance || 0);
                return (
                  <button
                    key={gift.key}
                    type="button"
                    onClick={() => handleGiftClick(gift)}
                    disabled={sendingKey && sendingKey !== gift.key}
                    className={`flex flex-col items-center justify-center rounded-2xl border px-2 py-2.5 text-center text-xs sm:text-sm transition-all
                      ${
                        disabled
                          ? "border-white/10 bg-white/5 text-white/60 opacity-70"
                          : "border-white/15 bg-white/5 hover:bg-white/10 text-white"
                      }`}
                  >
                    <div className="text-2xl mb-1">{gift.emoji}</div>
                    <div className="font-medium truncate w-full">{gift.name}</div>
                    <div className="text-[11px] text-pink-200 mt-0.5">{gift.cost} 💜</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Elite Gifts */}
        {!loading && eliteGifts.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <h3 className="text-xs sm:text-sm text-white/80 font-semibold">Elite Gifts</h3>
                <EliteBadge size="sm" />
              </div>
              {!isElite && <span className="text-[11px] text-amber-200/80">Exclusive to Elite members</span>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
              {eliteGifts.map((gift) => {
                const locked = !isElite;
                const disabled =
                  sendingKey === gift.key ||
                  (gift.cost || 0) > (balance || 0) ||
                  locked;
                return (
                  <button
                    key={gift.key}
                    type="button"
                    onClick={() => handleGiftClick(gift)}
                    disabled={sendingKey && sendingKey !== gift.key}
                    className={`relative flex flex-col items-center justify-center rounded-2xl border px-2 py-2.5 text-center text-xs sm:text-sm transition-all
                      ${
                        locked
                          ? "border-amber-300/40 bg-amber-200/5 text-amber-100/70"
                          : "border-amber-300/70 bg-gradient-to-b from-amber-200/20 to-yellow-500/10 text-amber-50"
                      }`}
                  >
                    <div className="text-2xl mb-1 drop-shadow-[0_0_8px_rgba(250,204,21,0.8)]">{gift.emoji}</div>
                    <div className="font-semibold truncate w-full">{gift.name}</div>
                    <div className="text-[11px] text-amber-200 mt-0.5">{gift.cost} 💜</div>
                    {locked && (
                      <div className="absolute inset-0 rounded-2xl bg-black/40 flex items-center justify-center text-[10px] sm:text-[11px] font-semibold text-amber-100/90">
                        👑 Upgrade to Elite
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Buy Coins / Footer */}
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <button
            type="button"
            onClick={handleBuyCoins}
            className="inline-flex items-center justify-center rounded-full bg-white text-pink-600 font-semibold text-xs sm:text-sm px-4 py-2 shadow-md hover:bg-pink-50"
            data-allow-white="1"
          >
            💜 Buy More LoveCoins
          </button>
          <p className="text-[11px] sm:text-xs text-white/60 text-center sm:text-right">
            Gifts help you stand out and build real connections.
          </p>
        </div>

        {/* LoveCoin Store Modal (inline, simple dev version) */}
        {showStore && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
            <div className="w-full max-w-md rounded-3xl bg-slate-950/95 p-5 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-slate-50">
                    LoveCoin Packs (Dev)
                  </h3>
                  <p className="text-xs text-slate-400">
                    Instantly credit LoveCoins for testing purchases.
                  </p>
                </div>
                <button
                  onClick={() => setShowStore(false)}
                  className="inline-flex items-center justify-center rounded-full bg-slate-900 p-1.5 text-slate-400 hover:bg-slate-800"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {loadingPacks ? (
                <div className="py-10 text-center text-xs text-slate-400">
                  Loading packs…
                </div>
              ) : (
                <div className="space-y-3">
                  {packs.map((pack) => (
                    <div
                      key={pack.pack_key}
                      className="flex items-center justify-between rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-3"
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-slate-50">
                          {pack.name}
                        </span>
                        <span className="text-xs text-slate-400">
                          {pack.coins.toLocaleString()} LoveCoins · $
                          {pack.price_usd.toFixed(2)} (dev)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDevPurchase(pack.pack_key)}
                        className="rounded-full bg-pink-500 px-3 py-1 text-xs font-semibold text-white hover:bg-pink-400"
                      >
                        Add
                      </button>
                    </div>
                  ))}
                  {!packs.length && (
                    <div className="py-6 text-center text-xs text-slate-500">
                      No packs available.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
